import { memo, useEffect, useRef } from "react";
import { CURVE_SAMPLES, sampleProfile } from "./velocity-curve.js";
import { repoName } from "../../lib/format.js";
import type { StreamLane } from "./velocity-streams.js";

/**
 * The stream field: every project is a current of particles, and nothing else.
 * There is no line and no filled shape — the band a project occupies *is* its
 * particles. How far they spread vertically is the code volume of that week,
 * how many there are is the merge-request count, and how fast they run is this
 * week's pace. Two projects with the same volume still look different: thirty
 * small changes is a dense fast current, three large ones a sparse wide one.
 *
 * All lanes render from a single WebGL context. When WebGL2 is missing or the
 * visitor asked for reduced motion, `onUnsupported` lets the parent fall back
 * to the static ribbons instead.
 */

const MAX_LANES = 8;

const VERT = `#version 300 es
precision highp float;

in float aSeed;
in float aLane;
in float aLateral;
in float aSize;

uniform sampler2D uProfiles;   // x: sample, y: lane. r = thickness, g = density
uniform vec2 uResolution;      // CSS px
uniform float uLaneHeight;     // CSS px
uniform float uTime;
uniform float uDpr;
uniform float uSpeed[${MAX_LANES}];
uniform float uTint[${MAX_LANES}];

out float vThickness;
out float vTint;
out float vFade;

/** Manual horizontal lerp: the texture is NEAREST so lanes never bleed. */
vec2 readProfile(float x, int lane) {
  float f = clamp(x, 0.0, 1.0) * float(${CURVE_SAMPLES} - 1);
  int i = int(floor(f));
  int j = min(i + 1, ${CURVE_SAMPLES} - 1);
  vec2 a = texelFetch(uProfiles, ivec2(i, lane), 0).rg;
  vec2 b = texelFetch(uProfiles, ivec2(j, lane), 0).rg;
  return mix(a, b, f - float(i));
}

void main() {
  int lane = int(aLane + 0.5);
  float speed = uSpeed[lane];
  float tint = uTint[lane];

  float lanePace = 0.6 + aSeed * 0.85;
  float x = fract(aSeed * 7.13 + uTime * speed * lanePace * 0.075);

  vec2 profile = readProfile(x, lane);
  float thickness = profile.r;
  vThickness = thickness;
  vTint = tint;

  // The spread IS the graph: a week with more changed code pushes its particles
  // further from the centre line, a quiet week collapses them into a thread.
  float halfMax = uLaneHeight * 0.5 - 3.0;
  float spread = max(1.2, thickness * halfMax);
  float centerY = (aLane + 0.5) * uLaneHeight;
  // A mild bias toward the core, so the current has a denser centre without
  // collapsing into it — squaring the offset pulls far too much weight inward.
  float shaped = sign(aLateral) * pow(abs(aLateral), 1.25);
  // A slow transverse wave, different per particle, so the band moves like a
  // current instead of sitting there as a ruled band of dots.
  float wave = sin(x * 9.0 + aSeed * 6.283 + uTime * 0.7) * spread * 0.18;
  float y = centerY + shaped * spread + wave;

  vFade = smoothstep(0.0, 0.04, x) * (1.0 - smoothstep(0.96, 1.0, x));

  gl_Position = vec4(x * 2.0 - 1.0, 1.0 - (y / uResolution.y) * 2.0, 0.0, 1.0);
  gl_PointSize = aSize * (0.85 + thickness * 0.9) * uDpr;
}`;

const FRAG = `#version 300 es
precision highp float;

uniform vec3 uLead;
uniform vec3 uTrail;
uniform float uOpacity;

in float vThickness;
in float vTint;
in float vFade;

out vec4 outColor;

void main() {
  vec2 d = gl_PointCoord - 0.5;
  float r = length(d) * 2.0;
  float alpha = 1.0 - smoothstep(0.25, 1.0, r);
  if (alpha <= 0.001) discard;

  // Rank is carried by tint alone — the fastest stream wears the full magenta
  // and the rest fade toward it. No second brand color enters the system.
  vec3 color = mix(uLead, uTrail, vTint);
  float strength = (0.6 + vThickness * 0.4) * vFade * uOpacity;
  outColor = vec4(color, alpha * strength);
}`;

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex.trim());
  if (!m) return [0.74, 0.15, 0.72];
  return [
    Number.parseInt(m[1]!, 16) / 255,
    Number.parseInt(m[2]!, 16) / 255,
    Number.parseInt(m[3]!, 16) / 255,
  ];
}

/** Compiles and reports. A silent shader failure is indistinguishable from a
 *  blank canvas, which is exactly how it hides — so it never fails quietly. */
function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn(
      `[Overdrive] ${type === gl.VERTEX_SHADER ? "vertex" : "fragment"} shader failed to compile:\n` +
        gl.getShaderInfoLog(shader),
    );
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function VelocityStreamFlowImpl({
  lanes,
  laneHeight,
  maxVolume,
  maxCount,
  leadColor,
  trailColor,
  onUnsupported,
}: {
  lanes: StreamLane[];
  laneHeight: number;
  maxVolume: number;
  maxCount: number;
  leadColor: string;
  trailColor: string;
  /** Called when this layer cannot render, so the parent can show the fallback. */
  onUnsupported: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const height = laneHeight * lanes.length;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || lanes.length === 0) return;

    const gl = canvas.getContext("webgl2", {
      alpha: true,
      antialias: false,
      premultipliedAlpha: false,
      powerPreference: "low-power",
    });
    if (!gl) {
      onUnsupported();
      return;
    }

    const vert = compile(gl, gl.VERTEX_SHADER, VERT);
    const frag = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vert || !frag) {
      onUnsupported();
      return;
    }
    const program = gl.createProgram();
    if (!program) {
      onUnsupported();
      return;
    }
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.warn(`[Overdrive] shader program failed to link:\n${gl.getProgramInfoLog(program)}`);
      onUnsupported();
      return;
    }
    gl.useProgram(program);

    const laneCount = Math.min(MAX_LANES, lanes.length);

    // Both profiles packed into one NEAREST texture: red is thickness, green is
    // density. One upload, one sampler, no per-lane uniform arrays to overflow.
    const pixels = new Uint8Array(CURVE_SAMPLES * laneCount * 4);
    const volumeProfiles = lanes.map((lane) => sampleProfile(lane.volume, maxVolume));
    const countProfiles = lanes.map((lane) => sampleProfile(lane.count, maxCount));
    for (let lane = 0; lane < laneCount; lane += 1) {
      for (let s = 0; s < CURVE_SAMPLES; s += 1) {
        const o = (lane * CURVE_SAMPLES + s) * 4;
        pixels[o] = Math.round(volumeProfiles[lane]![s]! * 255);
        pixels[o + 1] = Math.round(countProfiles[lane]![s]! * 255);
        pixels[o + 3] = 255;
      }
    }
    const texture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, CURVE_SAMPLES, laneCount, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.uniform1i(gl.getUniformLocation(program, "uProfiles"), 0);

    // Particles are handed out per lane by merge-request count, so density is
    // MR frequency while the vertical spread is code volume.
    const seeds: number[] = [];
    const laneIds: number[] = [];
    const laterals: number[] = [];
    const sizes: number[] = [];
    let s = 20260807;
    const rnd = () => {
      s = (s * 1103515245 + 12345) % 2147483648;
      return s / 2147483648;
    };
    lanes.slice(0, laneCount).forEach((lane, index) => {
      const mrs = lane.count.reduce((sum, v) => sum + v, 0);
      const n = Math.max(60, Math.min(900, Math.round(mrs * 14)));
      for (let i = 0; i < n; i += 1) {
        seeds.push(rnd());
        laneIds.push(index);
        laterals.push(rnd() * 2 - 1);
        sizes.push(1.3 + rnd() * 2.0);
      }
    });
    const total = seeds.length;

    const buffers: WebGLBuffer[] = [];
    function bindAttribute(name: string, values: number[]) {
      const location = gl!.getAttribLocation(program!, name);
      if (location < 0) return;
      const buffer = gl!.createBuffer();
      if (!buffer) return;
      buffers.push(buffer);
      gl!.bindBuffer(gl!.ARRAY_BUFFER, buffer);
      gl!.bufferData(gl!.ARRAY_BUFFER, new Float32Array(values), gl!.STATIC_DRAW);
      gl!.enableVertexAttribArray(location);
      gl!.vertexAttribPointer(location, 1, gl!.FLOAT, false, 0, 0);
    }
    bindAttribute("aSeed", seeds);
    bindAttribute("aLane", laneIds);
    bindAttribute("aLateral", laterals);
    bindAttribute("aSize", sizes);

    // Per-lane pace and rank tint. Clamped so a spike never strobes and a quiet
    // lane still drifts rather than freezing.
    const speeds = new Float32Array(MAX_LANES);
    const tints = new Float32Array(MAX_LANES);
    lanes.slice(0, laneCount).forEach((lane, index) => {
      const currentMrs = lane.count[lane.count.length - 1] ?? 0;
      speeds[index] = Math.max(0.7, Math.min(3.2, 0.7 + currentMrs * 0.16));
      tints[index] = laneCount > 1 ? index / (laneCount - 1) : 0;
    });
    gl.uniform1fv(gl.getUniformLocation(program, "uSpeed"), speeds);
    gl.uniform1fv(gl.getUniformLocation(program, "uTint"), tints);
    gl.uniform1f(gl.getUniformLocation(program, "uLaneHeight"), laneHeight);

    const lead = hexToRgb(leadColor);
    const trail = hexToRgb(trailColor);
    gl.uniform3f(gl.getUniformLocation(program, "uLead"), lead[0], lead[1], lead[2]);
    gl.uniform3f(gl.getUniformLocation(program, "uTrail"), trail[0], trail[1], trail[2]);

    const uResolution = gl.getUniformLocation(program, "uResolution");
    const uTime = gl.getUniformLocation(program, "uTime");
    const uDpr = gl.getUniformLocation(program, "uDpr");
    const uOpacity = gl.getUniformLocation(program, "uOpacity");

    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    function resize() {
      const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      const width = canvas!.clientWidth;
      canvas!.width = Math.round(width * dpr);
      canvas!.height = Math.round(height * dpr);
      gl!.viewport(0, 0, canvas!.width, canvas!.height);
      gl!.uniform2f(uResolution, width, height);
      gl!.uniform1f(uDpr, dpr);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // The field gathers rather than appearing: particles fade up over the first
    // moment, which is the one authored entrance this widget gets.
    const INTRO_MS = 900;

    let raf: number | undefined;
    let elapsed = 0;
    let lastFrame = 0;

    function frame(now: number) {
      if (gl!.isContextLost()) return;
      // Own clock, so pausing off screen resumes instead of jumping forward.
      if (lastFrame) elapsed += Math.min(64, now - lastFrame);
      lastFrame = now;

      const intro = Math.min(1, elapsed / INTRO_MS);
      gl!.uniform1f(uOpacity, intro * intro);
      gl!.uniform1f(uTime, elapsed / 1000);
      gl!.clearColor(0, 0, 0, 0);
      gl!.clear(gl!.COLOR_BUFFER_BIT);
      gl!.drawArrays(gl!.POINTS, 0, total);
      raf = requestAnimationFrame(frame);
    }

    function start() {
      if (raf !== undefined) return;
      lastFrame = 0;
      raf = requestAnimationFrame(frame);
    }
    function stop() {
      if (raf === undefined) return;
      cancelAnimationFrame(raf);
      raf = undefined;
    }

    const io = new IntersectionObserver(([entry]) => (entry?.isIntersecting ? start() : stop()), {
      threshold: 0.01,
    });
    io.observe(canvas);
    const onVisibility = () => (document.hidden ? stop() : start());
    document.addEventListener("visibilitychange", onVisibility);
    const onContextLost = (event: Event) => {
      event.preventDefault();
      stop();
    };
    canvas.addEventListener("webglcontextlost", onContextLost);

    return () => {
      stop();
      io.disconnect();
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      // Release GPU objects but never force context loss: a canvas whose
      // context was lost hands the same dead context to the next getContext,
      // which would leave this layer blank after any remount.
      buffers.forEach((buffer) => gl.deleteBuffer(buffer));
      gl.deleteTexture(texture);
      gl.deleteProgram(program);
      gl.deleteShader(vert);
      gl.deleteShader(frag);
    };
  }, [lanes, laneHeight, maxVolume, maxCount, leadColor, trailColor, height, onUnsupported]);

  const description = lanes
    .map((lane) => `${repoName(lane.projectPath)}: ${lane.volume[lane.volume.length - 1] ?? 0} geänderte Zeilen diese Woche`)
    .join("; ");

  return <canvas ref={canvasRef} className="block h-full w-full" role="img" aria-label={description} />;
}

/** Memoized: the tooltip above re-renders on every mouse move, and this layer
 *  must not rebuild its canvas when only the cursor moved. */
export const VelocityStreamFlow = memo(VelocityStreamFlowImpl);
